import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Send, Edit, Trash2, Copy, Calendar,
  Clock, MapPin, User, AlertTriangle, Check, X,
  Mail, MessageSquare, ExternalLink, Loader, FileText, Download,
  ToggleLeft, ToggleRight, RefreshCw
} from 'lucide-react';
import { formatFullName, formatDate, formatCurrency } from '../utils/helpers';
import { estimateStatuses, homeConditions, cleaningFrequencies } from '../utils/estimateData';
import { sendEstimateEmail } from '../utils/emailApi';
import { sendSMS } from '../utils/notificationsApi';
import Modal from '../components/Modal';
import './EstimateDetail.css';

function EstimateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    getEstimateById, getClientById, getPropertyById,
    getJobById, getInvoiceById,
    sendEstimate, renewEstimate, deleteEstimate, updateEstimate, dispatch, logClientCommunication
  } = useApp();

  const estimate = getEstimateById(id);
  const client = estimate ? getClientById(estimate.clientId) : null;
  const property = estimate ? getPropertyById(estimate.propertyId) : null;
  const convertedJob = estimate?.convertedToJobId ? getJobById(estimate.convertedToJobId) : null;
  const depositInvoice = estimate?.depositInvoiceId ? getInvoiceById(estimate.depositInvoiceId) : null;

  const [showSendModal, setShowSendModal] = useState(false);
  const [sendMethod, setSendMethod] = useState('email');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmChecked, setDeleteConfirmChecked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [isRenewing, setIsRenewing] = useState(false);
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.email === 'sarah@twosisterscleaningco.com' || user?.email === 'sarah.keel@twosisterscleaningco.com';

  // Determine if this is a custom estimate
  const isCustomEstimate = estimate?.source === 'custom_estimate_builder' ||
    estimate?.serviceType === 'commercial' ||
    estimate?.serviceType === 'hoarding' ||
    estimate?.serviceType === 'junk_removal' ||
    estimate?.serviceType === 'custom' ||
    (estimate?.lineItems?.length > 0 && !estimate?.calculatorInputs?.sqft && !estimate?.calculatorInputs?.condition);

  const handleToggleEstimateType = () => {
    const newType = isCustomEstimate ? 'residential' : 'custom';
    dispatch({
      type: 'UPDATE_ESTIMATE',
      payload: {
        id: estimate.id,
        serviceType: newType,
        source: newType === 'custom' ? 'custom_estimate_builder' : estimate.source
      }
    });
  };

  if (!estimate) {
    return (
      <div className="estimate-detail-page">
        <div className="not-found">
          <h2>Estimate not found</h2>
          <p>The estimate you're looking for doesn't exist.</p>
          <Link to="/admin/estimates" className="btn btn-primary">
            Back to Estimates
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = estimateStatuses[estimate.status];
  const conditionConfig = homeConditions[estimate.calculatorInputs?.condition];
  const frequencyFallbacks = { deep_clean: 'Deep Clean', move_in: 'Move In', move_out: 'Move Out' };
  const frequencyConfig = cleaningFrequencies[estimate.calculatorInputs?.frequency]
    || (frequencyFallbacks[estimate.calculatorInputs?.frequency] ? { label: frequencyFallbacks[estimate.calculatorInputs.frequency] } : null);

  // Debug logging
  console.log('Estimate status:', estimate.status, 'StatusConfig:', statusConfig);

  // Generate a token for the approval link
  const generateToken = () => {
    return Math.random().toString(36).substr(2, 12) + Math.random().toString(36).substr(2, 12);
  };

  // Preview the client portal link (generates token if needed)
  const handlePreviewPortal = async () => {
    let token = estimate.approvalToken;
    if (!token) {
      token = generateToken();
      // Save token to Firestore so the link works
      await updateEstimate(estimate.id, { approvalToken: token });
    }
    window.open(`/portal/approve/${token}`, '_blank');
  };

  const handleSendEstimate = async () => {
    // Always generate token first
    const newToken = estimate.approvalToken || generateToken();
    const approvalLink = `${window.location.origin}/portal/approve/${newToken}`;
    
    setIsSending(true);
    setSendError(null);
    
    console.log('Sending estimate:', {
      method: sendMethod,
      clientEmail: client?.email,
      clientPhone: client?.phone,
      estimateId: estimate.id,
      approvalLink
    });
    
    try {
      let deliverySucceeded = false;
      const commLogId = `log_${Date.now()}`;

      if (sendMethod === 'email') {
        if (!client?.email) {
          throw new Error('No email address on file for this client');
        }

        // Detect custom estimates - exclude residential calculator estimates which also have lineItems
        const isCustomEstimate = estimate.source === 'custom_estimate_builder' ||
          estimate.serviceType === 'commercial' ||
          estimate.serviceType === 'hoarding' ||
          (estimate.lineItems && estimate.lineItems.length > 0 && !estimate.calculatorInputs?.sqft);
        if (isCustomEstimate) {
          // Send custom estimate email with line items
          const emailResponse = await fetch('/.netlify/functions/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'custom_estimate',
              data: {
                clientId: client?.id,
                commLogId,
                clientName: client.firstName || 'there',
                clientEmail: client.email,
                clientPhone: client.phone || '',
                estimateTitle: estimate.title || 'Custom Service',
                lineItems: estimate.lineItems || [],
                subtotal: estimate.subtotal || estimate.total || 0,
                discountType: estimate.discountType,
                discountValue: estimate.discountValue,
                discountAmount: estimate.discountAmount || 0,
                total: estimate.total || 0,
                notes: estimate.notes || '',
                validDays: 30,
                propertyAddress: property?.addressLine1 || estimate.propertyAddress || 'Your property',
                estimateId: estimate.id,
                approvalLink
              }
            })
          });
          
          let result;
          try {
            result = await emailResponse.json();
          } catch (parseError) {
            throw new Error(`Email server returned invalid response (status ${emailResponse.status})`);
          }
          if (!emailResponse.ok) {
            throw new Error(result.error || result.details || `Email failed with status ${emailResponse.status}`);
          }
          console.log('Custom estimate email result:', result);
        } else {
          // Send standard estimate email
          const emailData = {
            clientId: client?.id,
            commLogId,
            clientEmail: client.email,
            clientName: client.firstName || 'there',
            estimateTotal: estimate.recurringTotal || estimate.deepCleanTotal || estimate.total || 0,
            approvalLink,
            propertyAddress: property?.addressLine1 || 'Your property',
            services: frequencyConfig?.label || 'Cleaning service',
            deepCleanTotal: estimate.skipDeepClean ? 0 : (estimate.deepCleanTotal || 0),
            recurringTotal: estimate.recurringTotal || 0,
            frequency: estimate.calculatorInputs?.frequency
          };
          console.log('Email data:', emailData);
          
          const emailResult = await sendEstimateEmail(emailData);
          console.log('Email result:', emailResult);
        }
        deliverySucceeded = true;
        
      } else if (sendMethod === 'text') {
        if (!client?.phone) {
          throw new Error('No phone number on file for this client');
        }
        
        console.log('Sending SMS to:', client.phone);
        const smsResult = await sendSMS('estimate_ready', {
          phone: client.phone,
          approvalLink
        });
        console.log('SMS result:', smsResult);
        
        // sendSMS returns { success: false } instead of throwing
        if (!smsResult || !smsResult.success) {
          throw new Error(smsResult?.error || 'SMS delivery failed - check Twilio configuration in Netlify');
        }
        deliverySucceeded = true;
        
      } else {
        // Copy link method - no delivery needed
        deliverySucceeded = true;
      }
      
      // ONLY mark as sent after confirmed delivery
      if (deliverySucceeded) {
        await sendEstimate(estimate.id, { sentVia: sendMethod, token: newToken });
        
        // Log successful communication (don't let logging failure block the UI)
        try {
          if (client?.id) {
            await logClientCommunication(client.id, {
              id: sendMethod === 'email' ? commLogId : undefined,
              type: sendMethod === 'email' ? 'email' : (sendMethod === 'text' ? 'sms' : 'link'),
              category: 'estimate',
              subject: 'Estimate Ready',
              recipient: sendMethod === 'email' ? client.email : (sendMethod === 'text' ? client.phone : 'link'),
              status: 'sent',
              details: {
                estimateId: estimate.id,
                estimateTotal: estimate.recurringTotal || estimate.deepCleanTotal || 0
              }
            });
          }
        } catch (logError) {
          console.warn('Communication log failed (non-blocking):', logError);
        }
        
        setShowSendModal(false);
      }
      
    } catch (error) {
      console.error('Failed to send estimate:', error);
      const errorMessage = error?.message || 'Unknown error';
      
      // Log failed communication (don't let logging failure cause additional issues)
      try {
        if (client?.id) {
          await logClientCommunication(client.id, {
            type: sendMethod === 'email' ? 'email' : 'sms',
            category: 'estimate',
            subject: 'Estimate Ready',
            recipient: sendMethod === 'email' ? client?.email : client?.phone,
            status: 'failed',
            error: errorMessage,
            details: { estimateId: estimate.id }
          });
        }
      } catch (logError) {
        console.warn('Failed to log communication failure:', logError);
      }
      
      setSendError(`Failed to send ${sendMethod === 'email' ? 'email' : 'text'}: ${errorMessage}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleRenewEstimate = async () => {
    setIsRenewing(true);
    try {
      await renewEstimate(estimate.id);
    } catch (error) {
      console.error('Failed to renew estimate:', error);
      alert('Failed to renew estimate. Please try again.');
    } finally {
      setIsRenewing(false);
    }
  };

  const handleDeleteEstimate = async () => {
    // Double-check permissions
    if ((estimate.status === 'approved' || estimate.status === 'converted') && !isAdmin) {
      alert('Only administrators can delete approved or converted estimates.');
      return;
    }
    
    if (!deleteConfirmChecked) {
      alert('Please check the confirmation box to proceed.');
      return;
    }
    
    setIsDeleting(true);
    
    try {
      await deleteEstimate(estimate.id, { isAdmin });
      navigate('/admin/estimates');
    } catch (error) {
      console.error('Error deleting estimate:', error);
      alert('Failed to delete estimate. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleApproveInternally = () => {
    // Mark all line items as approved
    const approvedLineItems = estimate.lineItems?.map(item => ({
      ...item,
      approvedByClient: true
    })) || [];

    dispatch({
      type: 'APPROVE_ESTIMATE',
      payload: {
        id: estimate.id,
        approvedLineItems,
        approvedRecurringTotal: estimate.recurringTotal,
        approvedDeepCleanTotal: estimate.deepCleanTotal,
        approvedBy: 'internal', // Mark as internally approved
        clientNotes: ''
      }
    });
  };

  const copyPortalLink = async () => {
    let token = estimate.approvalToken;
    
    // If no token exists, generate one and save it
    if (!token) {
      token = generateToken();
      await sendEstimate(estimate.id, { sentVia: 'link', token });
    }
    
    const link = `${window.location.origin}/portal/approve/${token}`;
    navigator.clipboard.writeText(link);
    alert('Portal link copied to clipboard!');
  };

  return (
    <div className="estimate-detail-page">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/admin/estimates')}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="header-title-row">
              <h1>Estimate for {client ? formatFullName(client) : 'Unknown'}</h1>
              <div 
                className="status-badge"
                style={{ 
                  color: statusConfig?.color,
                  background: statusConfig?.bgColor
                }}
              >
                {statusConfig?.label}
              </div>
            </div>
            <p className="header-subtitle">
              Created {formatDate(estimate.createdAt)}
              {estimate.sentAt && ` • Sent ${formatDate(estimate.sentAt)}`}
            </p>
            {isAdmin && !estimate.convertedToJobId && (
              <div className="estimate-type-toggle" onClick={handleToggleEstimateType} title="Toggle between Residential and Custom estimate type">
                {isCustomEstimate ? <ToggleRight size={22} className="toggle-icon active" /> : <ToggleLeft size={22} className="toggle-icon" />}
                <span className={`toggle-label ${isCustomEstimate ? 'custom' : 'residential'}`}>
                  {isCustomEstimate ? 'Custom' : 'Residential'}
                </span>
              </div>
            )}
          </div>
        </div>
        
        <div className="header-actions">
          {/* Preview portal link - always available */}
          {!estimate.convertedToJobId && (
            <button 
              className="btn btn-secondary"
              onClick={handlePreviewPortal}
              title="Preview what the client will see"
            >
              <ExternalLink size={18} />
              Preview
            </button>
          )}

          {/* Edit - available unless converted to job */}
          {!estimate.convertedToJobId && (
            <button 
              className="btn btn-secondary"
              onClick={() => navigate(isCustomEstimate ? `/admin/estimates/custom/${estimate.id}` : `/admin/estimates/${estimate.id}/edit`)}
            >
              <Edit size={18} />
              Edit
            </button>
          )}
          
          {/* Send/Resend button - show for draft, pending_review, or unknown status */}
          {(!estimate.status || estimate.status === 'draft' || estimate.status === 'pending_review') && (
            <>
              <button 
                className="btn btn-secondary"
                onClick={() => handleApproveInternally()}
                title="Approve without sending to client"
              >
                <Check size={18} />
                Approve Internally
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => setShowSendModal(true)}
              >
                <Send size={18} />
                Send to Client
              </button>
            </>
          )}
          
          {/* Sent actions */}
          {estimate.status === 'sent' && (
            <>
              <button className="btn btn-secondary" onClick={copyPortalLink}>
                <Copy size={18} />
                Copy Link
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => handleApproveInternally()}
                title="Approve on behalf of client"
              >
                <Check size={18} />
                Approve
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowSendModal(true)}
              >
                <Send size={18} />
                Resend
              </button>
            </>
          )}
          
          {/* Approved actions */}
          {estimate.status === 'approved' && !estimate.convertedToJobId && (
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/admin/estimates/${estimate.id}/schedule`)}
            >
              <Calendar size={18} />
              Schedule Job
            </button>
          )}

          {/* Expired/Declined actions */}
          {(estimate.status === 'expired' || estimate.status === 'declined') && (
            <>
              <button
                className="btn btn-primary"
                onClick={handleRenewEstimate}
                disabled={isRenewing}
              >
                {isRenewing ? <Loader size={18} className="spin" /> : <RefreshCw size={18} />}
                {isRenewing ? 'Renewing...' : 'Renew Estimate'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(isCustomEstimate ? `/admin/estimates/custom/${estimate.id}` : `/admin/estimates/${estimate.id}/edit`)}
              >
                <Edit size={18} />
                Edit & Resend
              </button>
            </>
          )}

          {/* Delete - not available if converted */}
          {!estimate.convertedToJobId && (
            <button 
              className="btn btn-icon btn-delete"
              onClick={() => setShowDeleteModal(true)}
              title="Delete estimate"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Review Warning - for pending_review or draft with requiresReview */}
      {((estimate.status === 'pending_review') || (estimate.requiresReview && estimate.status === 'draft')) && (
        <div className="review-warning">
          <AlertTriangle size={20} />
          <div className="review-content">
            <strong>Manager Review Required</strong>
            {estimate.source === 'website_intake' ? (
              <>
                <p>This quote was submitted online and needs your review before sending.</p>
                <div className="review-checklist">
                  <div className={`checklist-item ${estimate.calculatorInputs?.sqft ? 'complete' : 'incomplete'}`}>
                    {estimate.calculatorInputs?.sqft ? <Check size={16} /> : <X size={16} />}
                    <span>Square footage {estimate.calculatorInputs?.sqft ? `(${estimate.calculatorInputs.sqft.toLocaleString()} sqft)` : '— REQUIRED'}</span>
                  </div>
                  <div className={`checklist-item ${estimate.calculatorInputs?.condition ? 'complete' : 'incomplete'}`}>
                    {estimate.calculatorInputs?.condition ? <Check size={16} /> : <X size={16} />}
                    <span>Home condition verified</span>
                  </div>
                  <div className={`checklist-item ${estimate.intakePhotos?.length > 0 ? 'complete' : 'neutral'}`}>
                    {estimate.intakePhotos?.length > 0 ? <Check size={16} /> : <span className="dash">—</span>}
                    <span>Photos reviewed ({estimate.intakePhotos?.length || 0} uploaded)</span>
                  </div>
                  <div className={`checklist-item ${estimate.recurringTotal > 0 ? 'complete' : 'incomplete'}`}>
                    {estimate.recurringTotal > 0 ? <Check size={16} /> : <X size={16} />}
                    <span>Pricing calculated {estimate.recurringTotal > 0 ? `(${formatCurrency(estimate.recurringTotal)})` : ''}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p>This estimate needs review before it can be sent.</p>
                <ul>
                  {estimate.reviewReasons?.map((reason, idx) => (
                    <li key={idx}>{reason}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className="review-actions">
            <button
              className="btn btn-secondary"
              onClick={() => navigate(isCustomEstimate ? `/admin/estimates/custom/${estimate.id}` : `/admin/estimates/${estimate.id}/edit`)}
            >
              <Edit size={16} />
              Edit Estimate
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => {
                // Validate before allowing send
                if (!estimate.calculatorInputs?.sqft) {
                  alert('Please add square footage before sending the estimate.');
                  return;
                }
                if (!estimate.recurringTotal || estimate.recurringTotal === 0) {
                  alert('Please calculate pricing before sending the estimate.');
                  return;
                }
                // Mark as reviewed and open send modal
                dispatch({
                  type: 'UPDATE_ESTIMATE',
                  payload: {
                    id: estimate.id,
                    status: 'draft',
                    requiresReview: false,
                    reviewedAt: new Date().toISOString()
                  }
                });
                setShowSendModal(true);
              }}
              disabled={!estimate.calculatorInputs?.sqft || !estimate.recurringTotal}
            >
              <Check size={16} />
              Approve & Send
            </button>
          </div>
        </div>
      )}

      <div className="detail-layout">
        {/* Left Column: Estimate Details */}
        <div className="detail-main">
          {/* Client & Property Info */}
          <div className="detail-card">
            <h3>Client & Property</h3>
            <div className="info-grid">
              <div className="info-item">
                <User size={18} />
                <div>
                  <label>Client</label>
                  <p>
                    <Link to={`/clients/${client?.id}`}>
                      {client ? formatFullName(client) : 'Unknown'}
                    </Link>
                  </p>
                  <span className="meta">{client?.phone}</span>
                </div>
              </div>
              <div className="info-item">
                <MapPin size={18} />
                <div>
                  <label>Property</label>
                  <p>{property?.street || property?.addressLine1 || estimate.propertyAddress?.split(',')[0] || 'No property'}</p>
                  <span className="meta">
                    {property?.city || estimate.propertyAddress?.split(',').slice(1).join(',').trim() || ''}, {property?.state || ''}
                  </span>
                </div>
              </div>
              {!isCustomEstimate && (
                <>
                  <div className="info-item">
                    <Clock size={18} />
                    <div>
                      <label>Service Frequency</label>
                      <p>{frequencyConfig?.label || '—'}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="condition-icon">{conditionConfig?.icon}</div>
                    <div>
                      <label>Home Condition</label>
                      <p>{conditionConfig?.label || '—'}</p>
                    </div>
                  </div>
                </>
              )}
              {isCustomEstimate && estimate.title && (
                <div className="info-item">
                  <FileText size={18} />
                  <div>
                    <label>Estimate Title</label>
                    <p>{estimate.title}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Property Details - residential only */}
          {!isCustomEstimate && (
            <div className="detail-card">
              <h3>Property Details</h3>
              <div className="property-stats">
                <div className="stat">
                  <span className="stat-value">
                    {estimate.calculatorInputs?.sqft?.toLocaleString() || '—'}
                  </span>
                  <span className="stat-label">Sqft</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{estimate.calculatorInputs?.bedrooms || '—'}</span>
                  <span className="stat-label">Bedrooms</span>
                </div>
                <div className="stat">
                  <span className="stat-value">
                    {(estimate.calculatorInputs?.fullBaths || 0) +
                     (estimate.calculatorInputs?.halfBaths || 0) * 0.5}
                  </span>
                  <span className="stat-label">Bathrooms</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{estimate.calculatorInputs?.people || '—'}</span>
                  <span className="stat-label">People</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{estimate.calculatorInputs?.sheddingPets || 0}</span>
                  <span className="stat-label">Shedding Pets</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{estimate.calculatorInputs?.glassShowerDoors || 0}</span>
                  <span className="stat-label">Glass Showers</span>
                </div>
              </div>
            </div>
          )}

          {/* Property Info - custom estimates (sqft/bedrooms/bathrooms if set) */}
          {isCustomEstimate && estimate.propertyInfo &&
            (estimate.propertyInfo.sqft || estimate.propertyInfo.bedrooms || estimate.propertyInfo.bathrooms || estimate.propertyInfo.propertyNotes) && (
              <div className="detail-card">
                <h3>Property Info</h3>
                <div className="property-stats">
                  {estimate.propertyInfo.sqft && (
                    <div className="stat">
                      <span className="stat-value">{Number(estimate.propertyInfo.sqft).toLocaleString()}</span>
                      <span className="stat-label">Sqft</span>
                    </div>
                  )}
                  {estimate.propertyInfo.bedrooms && (
                    <div className="stat">
                      <span className="stat-value">{estimate.propertyInfo.bedrooms}</span>
                      <span className="stat-label">Bedrooms</span>
                    </div>
                  )}
                  {estimate.propertyInfo.bathrooms && (
                    <div className="stat">
                      <span className="stat-value">{estimate.propertyInfo.bathrooms}</span>
                      <span className="stat-label">Bathrooms</span>
                    </div>
                  )}
                </div>
                {estimate.propertyInfo.propertyNotes && (
                  <p className="property-notes">{estimate.propertyInfo.propertyNotes}</p>
                )}
              </div>
            )}

          {/* Intake Form Data (if from website) */}
          {estimate.source === 'website_intake' && (
            <>
              {/* Requested Add-ons */}
              {estimate.selectedAddons?.length > 0 && (
                <div className="detail-card">
                  <h3>🛎️ Requested Add-ons</h3>
                  <div className="requested-addons">
                    {estimate.selectedAddons.map((addon, idx) => (
                      <div key={idx} className="addon-item">
                        <span className="addon-label">{addon.label || addon.id}</span>
                        {addon.quantity && <span className="addon-qty">× {addon.quantity}</span>}
                        {addon.hours && <span className="addon-qty">{addon.hours} hours</span>}
                        {addon.isBlueCeramic && <span className="addon-note">⚠️ Blue ceramic oven</span>}
                        {addon.requiresReview && <span className="addon-note">Needs custom quote</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Special Requests & Notes */}
              {(estimate.specialRequests || estimate.productConcerns || estimate.approvedProducts === false) && (
                <div className="detail-card">
                  <h3>📝 Client Notes</h3>
                  
                  {estimate.approvedProducts === false && (
                    <div className="client-note warning">
                      <strong>⚠️ Product Concerns:</strong>
                      <p>{estimate.productConcerns || 'Client has concerns about cleaning products'}</p>
                    </div>
                  )}
                  
                  {estimate.specialRequests && (
                    <div className="client-note">
                      <strong>Special Requests:</strong>
                      <p>{estimate.specialRequests}</p>
                    </div>
                  )}
                  
                  {estimate.preferredDay && (
                    <p><strong>Preferred Day:</strong> {estimate.preferredDay}</p>
                  )}
                  {estimate.preferredTime && (
                    <p><strong>Preferred Time:</strong> {estimate.preferredTime}</p>
                  )}
                </div>
              )}

              {/* Uploaded Photos */}
              {estimate.intakePhotos?.length > 0 && (
                <div className="detail-card full-width">
                  <h3>📷 Client Photos ({estimate.intakePhotos.length})</h3>
                  <div className="intake-photos-grid">
                    {estimate.intakePhotos.map((photo, idx) => (
                      <div key={idx} className="intake-photo">
                        <img src={photo.preview} alt={photo.label || `Photo ${idx + 1}`} />
                        {photo.label && <span className="photo-label">{photo.label}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Line Items */}
          <div className="detail-card">
            <h3>{isCustomEstimate ? 'Line Items' : 'Recurring Service Line Items'}</h3>
            <div className="line-items-table">
              {estimate.lineItems?.map((item, idx) => {
                const label = item.label || item.service || item.description || 'Item';
                const hasQtyUnit = item.unitPrice > 0 && (item.qty != null || item.quantity != null);
                const qty = item.qty ?? item.quantity ?? 1;
                const displayPrice = item.price != null && item.price !== 0
                  ? item.price
                  : (item.unitPrice ? Math.round(qty * item.unitPrice * 100) / 100 : item.price);
                return (
                  <div key={idx} className={`line-item-row ${item.type || ''}`}>
                    <div className="item-info">
                      <span className="item-label">{label}</span>
                      {item.clientDescription && (
                        <span className="item-desc" style={{ color: '#2c5f4e', fontStyle: 'italic' }}>📋 {item.clientDescription}</span>
                      )}
                      {item.description && item.description !== label && (
                        <span className="item-desc">{item.description}</span>
                      )}
                      {hasQtyUnit && (
                        <span className="item-desc">
                          {qty} {item.uom || 'each'} × {formatCurrency(item.unitPrice)}
                        </span>
                      )}
                    </div>
                    <div className="item-status">
                      {item.approvedByClient === true && (
                        <span className="approved"><Check size={14} /> Approved</span>
                      )}
                      {item.approvedByClient === false && (
                        <span className="declined"><X size={14} /> Declined</span>
                      )}
                    </div>
                    <div className="item-price">
                      {displayPrice != null ? formatCurrency(displayPrice) : 'TBD'}
                    </div>
                  </div>
                );
              })}
              {isCustomEstimate ? (
                <>
                  {estimate.subtotal != null && estimate.discountAmount > 0 && (
                    <div className="line-item-row">
                      <div className="item-info">
                        <span className="item-label">Subtotal</span>
                      </div>
                      <div className="item-price">{formatCurrency(estimate.subtotal)}</div>
                    </div>
                  )}
                  {estimate.discountAmount > 0 && (
                    <div className="line-item-row">
                      <div className="item-info">
                        <span className="item-label">
                          Discount{estimate.discountReason ? ` – ${estimate.discountReason}` : ''}
                        </span>
                      </div>
                      <div className="item-price">-{formatCurrency(estimate.discountAmount)}</div>
                    </div>
                  )}
                  <div className="line-item-row total">
                    <div className="item-info">
                      <span className="item-label">Total</span>
                    </div>
                    <div className="item-price">
                      {formatCurrency(estimate.total ?? estimate.displayPrice ?? estimate.subtotal ?? 0)}
                    </div>
                  </div>
                </>
              ) : (
                <div className="line-item-row total">
                  <div className="item-info">
                    <span className="item-label">Recurring Total</span>
                  </div>
                  <div className="item-price">
                    {formatCurrency(estimate.recurringTotal)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Deep Clean Line Items */}
          {estimate.deepCleanLineItems?.length > 0 && (
            <div className="detail-card">
              <h3>Deep Clean Line Items</h3>
              <div className="line-items-table">
                {estimate.deepCleanLineItems?.map((item, idx) => (
                  <div key={idx} className={`line-item-row ${item.type}`}>
                    <div className="item-info">
                      <span className="item-label">{item.label}</span>
                      {item.clientDescription && (
                        <span className="item-desc" style={{ color: '#2c5f4e', fontStyle: 'italic' }}>📋 {item.clientDescription}</span>
                      )}
                      {item.description && (
                        <span className="item-desc">{item.description}</span>
                      )}
                    </div>
                    <div className="item-price">
                      {item.price !== null ? formatCurrency(item.price) : 'TBD'}
                    </div>
                  </div>
                ))}
                <div className="line-item-row total">
                  <div className="item-info">
                    <span className="item-label">Deep Clean Total</span>
                  </div>
                  <div className="item-price">
                    {formatCurrency(estimate.deepCleanTotal)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Summary & Actions */}
        <div className="detail-sidebar">
          {/* Price Summary */}
          <div className="summary-card">
            <h3>Price Summary</h3>

            {isCustomEstimate ? (
              <>
                {estimate.subtotal != null && estimate.discountAmount > 0 && (
                  <div className="price-row">
                    <span>Subtotal</span>
                    <span className="price">{formatCurrency(estimate.subtotal)}</span>
                  </div>
                )}
                {estimate.discountAmount > 0 && (
                  <div className="price-row">
                    <span>Discount</span>
                    <span className="price">-{formatCurrency(estimate.discountAmount)}</span>
                  </div>
                )}
                <div className="price-row recurring">
                  <span>Total</span>
                  <span className="price">
                    {formatCurrency(estimate.total ?? estimate.displayPrice ?? estimate.subtotal ?? 0)}
                  </span>
                </div>
              </>
            ) : (
              <>
                {estimate.deepCleanTotal && (
                  <div className="price-row">
                    <span>Deep Clean</span>
                    <span className="price">{formatCurrency(estimate.deepCleanTotal)}</span>
                  </div>
                )}

                <div className="price-row recurring">
                  <span>{frequencyConfig?.label || 'Service'}</span>
                  <span className="price">{formatCurrency(estimate.recurringTotal)}</span>
                </div>

                {estimate.status === 'approved' && estimate.approvedRecurringTotal !== estimate.recurringTotal && (
                  <div className="price-row approved">
                    <span>Approved Total</span>
                    <span className="price">{formatCurrency(estimate.approvedRecurringTotal)}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Timeline */}
          <div className="timeline-card">
            <h3>Timeline</h3>
            <div className="timeline">
              <div className="timeline-item completed">
                <div className="timeline-dot" />
                <div className="timeline-content">
                  <span className="timeline-label">Created</span>
                  <span className="timeline-date">{formatDate(estimate.createdAt, 'MMM d, h:mm a')}</span>
                </div>
              </div>
              
              {estimate.sentAt && (
                <div className="timeline-item completed">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <span className="timeline-label">
                      Sent via {estimate.sentVia}
                    </span>
                    <span className="timeline-date">{formatDate(estimate.sentAt, 'MMM d, h:mm a')}</span>
                  </div>
                </div>
              )}
              
              {estimate.approvedAt && (
                <div className="timeline-item completed">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <span className="timeline-label">Approved by client</span>
                    <span className="timeline-date">{formatDate(estimate.approvedAt, 'MMM d, h:mm a')}</span>
                  </div>
                </div>
              )}
              
              {estimate.declinedAt && (
                <div className="timeline-item declined">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <span className="timeline-label">Declined</span>
                    <span className="timeline-date">{formatDate(estimate.declinedAt, 'MMM d, h:mm a')}</span>
                    {estimate.declineReason && (
                      <span className="timeline-note">{estimate.declineReason}</span>
                    )}
                  </div>
                </div>
              )}
              
              {estimate.status === 'sent' && estimate.validUntil && (
                <div className="timeline-item pending">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <span className="timeline-label">Expires</span>
                    <span className="timeline-date">{formatDate(estimate.validUntil, 'MMM d, yyyy')}</span>
                  </div>
                </div>
              )}

              {estimate.status === 'expired' && estimate.validUntil && (
                <div className="timeline-item declined">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <span className="timeline-label">Expired</span>
                    <span className="timeline-date">{formatDate(estimate.validUntil, 'MMM d, yyyy')}</span>
                  </div>
                </div>
              )}

              {estimate.renewedAt && (
                <div className="timeline-item completed">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <span className="timeline-label">Renewed</span>
                    <span className="timeline-date">{formatDate(estimate.renewedAt, 'MMM d, h:mm a')}</span>
                  </div>
                </div>
              )}

              {(estimate.convertedAt || estimate.status === 'converted') && (
                <div className="timeline-item completed">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <span className="timeline-label">
                      Converted to {estimate.recurringScheduleId ? 'recurring service' : 'job'}
                    </span>
                    {estimate.convertedAt && (
                      <span className="timeline-date">{formatDate(estimate.convertedAt, 'MMM d, h:mm a')}</span>
                    )}
                    {convertedJob && (
                      <span className="timeline-note">
                        First job scheduled for {formatDate(convertedJob.date, 'MMM d, yyyy')}
                      </span>
                    )}
                    {estimate.convertedToJobId && !convertedJob && (
                      <span className="timeline-note">Job ID: {estimate.convertedToJobId}</span>
                    )}
                    {depositInvoice && (
                      <span className="timeline-note">
                        Deposit invoice:{' '}
                        <Link to={`/admin/invoices/${depositInvoice.id}`}>
                          {depositInvoice.invoiceNumber}
                        </Link>
                      </span>
                    )}
                    {estimate.depositInvoiceId && !depositInvoice && (
                      <span className="timeline-note">Deposit invoice ID: {estimate.depositInvoiceId}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Portal Link */}
          {estimate.approvalToken && (
            <div className="portal-link-card">
              <h3>Client Approval Link</h3>
              <p>Share this link with the client to approve the estimate:</p>
              <div className="link-box">
                <code>{window.location.origin}/portal/approve/{estimate.approvalToken}</code>
                <button onClick={copyPortalLink} title="Copy link">
                  <Copy size={16} />
                </button>
              </div>
              <a 
                href={`/portal/approve/${estimate.approvalToken}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-sm open-portal-link"
              >
                <ExternalLink size={16} />
                Open Portal Preview
              </a>
            </div>
          )}

          {/* Notes */}
          {(estimate.notes || estimate.internalNotes || estimate.clientNotes || (estimate.frequencyNotes && Object.values(estimate.frequencyNotes).some(n => n))) && (
            <div className="notes-card">
              <h3>Notes</h3>
              {estimate.notes && (
                <div className="note-section">
                  <label>Estimate Notes</label>
                  <p>{estimate.notes}</p>
                </div>
              )}
              {estimate.internalNotes && (
                <div className="note-section">
                  <label>Internal Notes</label>
                  <p>{estimate.internalNotes}</p>
                </div>
              )}
              {estimate.clientNotes && (
                <div className="note-section">
                  <label>Client Notes</label>
                  <p>{estimate.clientNotes}</p>
                </div>
              )}
              {estimate.frequencyNotes && Object.entries(estimate.frequencyNotes).filter(([, note]) => note).length > 0 && (
                <div className="note-section">
                  <label>Frequency Notes</label>
                  {Object.entries(estimate.frequencyNotes).filter(([, note]) => note).map(([freq, note]) => (
                    <div key={freq} style={{ marginBottom: '6px' }}>
                      <strong style={{ fontSize: '0.85rem' }}>{freq.replace('_', '-').replace(/\b\w/g, l => l.toUpperCase())}:</strong>
                      <p style={{ margin: '2px 0 0 0' }}>{note}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Original Lead Submission Details */}
          {(estimate.source?.includes('website') || estimate.calculatorInputs || estimate.clientNotes || estimate.specialInstructions) && (
            <div className="lead-submission-card">
              <div className="lead-submission-header">
                <h3><FileText size={16} /> Original Lead Submission</h3>
                <button 
                  className="btn-download-pdf"
                  onClick={() => {
                    // Generate and download PDF
                    const printContent = document.getElementById('lead-submission-content');
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Lead Submission - ${formatFullName(client) || 'Unknown'}</title>
                          <style>
                            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                            h1 { color: #1a2634; border-bottom: 2px solid #2c5f4e; padding-bottom: 10px; }
                            h2 { color: #2c5f4e; margin-top: 30px; }
                            .section { margin-bottom: 20px; }
                            .field { display: flex; margin-bottom: 8px; }
                            .label { font-weight: bold; width: 180px; color: #475569; }
                            .value { color: #1e293b; }
                            .notes-box { background: #f8fafc; padding: 15px; border-radius: 8px; margin-top: 10px; }
                            .header-info { color: #64748b; font-size: 14px; margin-bottom: 20px; }
                            @media print { body { padding: 20px; } }
                          </style>
                        </head>
                        <body>
                          <h1>Lead Submission Details</h1>
                          <div class="header-info">
                            <strong>Submitted:</strong> ${formatDate(estimate.createdAt)}<br/>
                            <strong>Source:</strong> ${estimate.source || 'Unknown'}<br/>
                            <strong>Estimate #:</strong> ${estimate.id}
                          </div>
                          
                          <h2>Contact Information</h2>
                          <div class="section">
                            <div class="field"><span class="label">Name:</span><span class="value">${formatFullName(client) || 'Unknown'}</span></div>
                            <div class="field"><span class="label">Email:</span><span class="value">${client?.email || 'Not provided'}</span></div>
                            <div class="field"><span class="label">Phone:</span><span class="value">${client?.phone || 'Not provided'}</span></div>
                            <div class="field"><span class="label">Client Type:</span><span class="value">${client?.clientType || 'Residential'}</span></div>
                            ${client?.businessName ? `<div class="field"><span class="label">Business Name:</span><span class="value">${client.businessName}</span></div>` : ''}
                          </div>
                          
                          <h2>Property Details</h2>
                          <div class="section">
                            <div class="field"><span class="label">Address:</span><span class="value">${estimate.address || property?.street || 'Not provided'}</span></div>
                            <div class="field"><span class="label">City/State/Zip:</span><span class="value">${[property?.city, property?.state, property?.zip].filter(Boolean).join(', ') || estimate.calculatorInputs?.city || 'Not provided'}</span></div>
                            <div class="field"><span class="label">Square Footage:</span><span class="value">${estimate.calculatorInputs?.sqft?.toLocaleString() || property?.sqft || 'Not provided'}</span></div>
                            <div class="field"><span class="label">Bedrooms:</span><span class="value">${estimate.calculatorInputs?.bedrooms || property?.bedrooms || 'Not provided'}</span></div>
                            <div class="field"><span class="label">Bathrooms:</span><span class="value">${(estimate.calculatorInputs?.fullBaths || 0) + (estimate.calculatorInputs?.halfBaths || 0) * 0.5 || property?.bathrooms || 'Not provided'}</span></div>
                            <div class="field"><span class="label">People in Home:</span><span class="value">${estimate.calculatorInputs?.people || 'Not provided'}</span></div>
                            <div class="field"><span class="label">Home Condition:</span><span class="value">${estimate.calculatorInputs?.condition ? homeConditions[estimate.calculatorInputs.condition]?.label || estimate.calculatorInputs.condition : 'Not provided'}</span></div>
                            <div class="field"><span class="label">Pets (Shedding):</span><span class="value">${estimate.calculatorInputs?.sheddingPets || property?.pets || 'None'}</span></div>
                            ${estimate.calculatorInputs?.petDetails || property?.petDetails ? `<div class="field"><span class="label">Pet Details:</span><span class="value">${estimate.calculatorInputs?.petDetails || property?.petDetails}</span></div>` : ''}
                            <div class="field"><span class="label">Glass Shower Doors:</span><span class="value">${estimate.calculatorInputs?.glassShowerDoors || 0}</span></div>
                          </div>
                          
                          <h2>Service Preferences</h2>
                          <div class="section">
                            <div class="field"><span class="label">Service Type:</span><span class="value">${estimate.serviceType || 'Residential'}</span></div>
                            <div class="field"><span class="label">Preferred Frequency:</span><span class="value">${estimate.calculatorInputs?.frequency ? cleaningFrequencies[estimate.calculatorInputs.frequency]?.label || estimate.calculatorInputs.frequency : estimate.frequency || 'Not specified'}</span></div>
                            ${estimate.preferredSchedule ? `<div class="field"><span class="label">Preferred Schedule:</span><span class="value">${estimate.preferredSchedule}</span></div>` : ''}
                            ${estimate.selectedAddons?.length > 0 ? `<div class="field"><span class="label">Add-on Services:</span><span class="value">${estimate.selectedAddons.map(a => typeof a === 'string' ? a : (a.label || a.id || a.name || 'Add-on')).join(', ')}</span></div>` : ''}
                          </div>
                          
                          ${estimate.accessInstructions || estimate.specialInstructions || estimate.clientNotes || client?.notes ? `
                          <h2>Notes & Instructions</h2>
                          <div class="section">
                            ${estimate.accessInstructions ? `<div class="notes-box"><strong>Access Instructions:</strong><br/>${estimate.accessInstructions}</div>` : ''}
                            ${estimate.specialInstructions ? `<div class="notes-box"><strong>Special Instructions:</strong><br/>${estimate.specialInstructions}</div>` : ''}
                            ${estimate.clientNotes ? `<div class="notes-box"><strong>Client Notes:</strong><br/>${estimate.clientNotes}</div>` : ''}
                            ${client?.notes ? `<div class="notes-box"><strong>Additional Notes:</strong><br/>${client.notes}</div>` : ''}
                          </div>
                          ` : ''}
                          
                          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 12px;">
                            Generated from Two Sisters Cleaning & Organizing Portal<br/>
                            ${new Date().toLocaleString()}
                          </div>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.print();
                  }}
                >
                  <Download size={14} />
                  Download PDF
                </button>
              </div>
              
              <div id="lead-submission-content" className="lead-submission-content">
                <div className="lead-section">
                  <h4>Contact Info</h4>
                  <div className="lead-fields">
                    <div className="lead-field">
                      <span className="lead-label">Name</span>
                      <span className="lead-value">{formatFullName(client) || '—'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">Email</span>
                      <span className="lead-value">{client?.email || '—'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">Phone</span>
                      <span className="lead-value">{client?.phone || '—'}</span>
                    </div>
                    {client?.businessName && (
                      <div className="lead-field">
                        <span className="lead-label">Business</span>
                        <span className="lead-value">{client.businessName}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="lead-section">
                  <h4>Property Details</h4>
                  <div className="lead-fields">
                    <div className="lead-field">
                      <span className="lead-label">Address</span>
                      <span className="lead-value">{estimate.address || property?.street || '—'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">Sq Ft</span>
                      <span className="lead-value">{estimate.calculatorInputs?.sqft?.toLocaleString() || property?.sqft || '—'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">Bedrooms</span>
                      <span className="lead-value">{estimate.calculatorInputs?.bedrooms || property?.bedrooms || '—'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">Bathrooms</span>
                      <span className="lead-value">{(estimate.calculatorInputs?.fullBaths || 0) + (estimate.calculatorInputs?.halfBaths || 0) * 0.5 || property?.bathrooms || '—'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">People</span>
                      <span className="lead-value">{estimate.calculatorInputs?.people || '—'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">Condition</span>
                      <span className="lead-value">{estimate.calculatorInputs?.condition ? (homeConditions[estimate.calculatorInputs.condition]?.label || estimate.calculatorInputs.condition) : '—'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">Shedding Pets</span>
                      <span className="lead-value">{estimate.calculatorInputs?.sheddingPets || property?.pets || '0'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">Glass Showers</span>
                      <span className="lead-value">{estimate.calculatorInputs?.glassShowerDoors || '0'}</span>
                    </div>
                  </div>
                </div>

                <div className="lead-section">
                  <h4>Service Preferences</h4>
                  <div className="lead-fields">
                    <div className="lead-field">
                      <span className="lead-label">Service Type</span>
                      <span className="lead-value">{estimate.serviceType || 'Residential'}</span>
                    </div>
                    <div className="lead-field">
                      <span className="lead-label">Frequency</span>
                      <span className="lead-value">{estimate.calculatorInputs?.frequency ? (cleaningFrequencies[estimate.calculatorInputs.frequency]?.label || estimate.calculatorInputs.frequency) : (estimate.frequency || '—')}</span>
                    </div>
                    {estimate.preferredSchedule && (
                      <div className="lead-field full-width">
                        <span className="lead-label">Preferred Schedule</span>
                        <span className="lead-value">{estimate.preferredSchedule}</span>
                      </div>
                    )}
                    {estimate.selectedAddons?.length > 0 && (
                      <div className="lead-field full-width">
                        <span className="lead-label">Add-ons Requested</span>
                        <span className="lead-value">{estimate.selectedAddons.map(a => typeof a === 'string' ? a : (a.label || a.id || a.name || 'Add-on')).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                {(estimate.accessInstructions || estimate.specialInstructions || estimate.clientNotes || client?.notes || estimate.calculatorInputs?.petDetails) && (
                  <div className="lead-section">
                    <h4>Notes & Instructions</h4>
                    <div className="lead-notes">
                      {estimate.accessInstructions && (
                        <div className="lead-note-item">
                          <span className="lead-note-label">Access Instructions</span>
                          <p>{estimate.accessInstructions}</p>
                        </div>
                      )}
                      {estimate.specialInstructions && (
                        <div className="lead-note-item">
                          <span className="lead-note-label">Special Instructions</span>
                          <p>{estimate.specialInstructions}</p>
                        </div>
                      )}
                      {estimate.clientNotes && (
                        <div className="lead-note-item">
                          <span className="lead-note-label">Client Notes</span>
                          <p>{estimate.clientNotes}</p>
                        </div>
                      )}
                      {estimate.calculatorInputs?.petDetails && (
                        <div className="lead-note-item">
                          <span className="lead-note-label">Pet Details</span>
                          <p>{estimate.calculatorInputs.petDetails}</p>
                        </div>
                      )}
                      {client?.notes && (
                        <div className="lead-note-item">
                          <span className="lead-note-label">Additional Notes</span>
                          <p>{client.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="lead-meta">
                  <span>Submitted: {formatDate(estimate.createdAt)}</span>
                  <span>Source: {estimate.source || 'Unknown'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Send Modal */}
      <Modal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        title="Send Estimate"
      >
        <div className="send-modal">
          <p>How would you like to send this estimate to {client ? formatFullName(client) : 'the client'}?</p>
          
          <div className="send-options">
            <label className={`send-option ${sendMethod === 'email' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="sendMethod"
                value="email"
                checked={sendMethod === 'email'}
                onChange={(e) => setSendMethod(e.target.value)}
                disabled={isSending}
              />
              <Mail size={24} />
              <span>Email</span>
              <span className="contact">{client?.email || 'No email'}</span>
            </label>
            
            <label className={`send-option ${sendMethod === 'text' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="sendMethod"
                value="text"
                checked={sendMethod === 'text'}
                onChange={(e) => setSendMethod(e.target.value)}
                disabled={isSending}
              />
              <MessageSquare size={24} />
              <span>Text Message</span>
              <span className="contact">{client?.phone || 'No phone'}</span>
            </label>
          </div>

          {sendError && (
            <div className="send-error">
              <AlertTriangle size={16} />
              {sendError}
            </div>
          )}

          <div className="modal-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowSendModal(false)}
              disabled={isSending}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleSendEstimate}
              disabled={isSending || (sendMethod === 'email' && !client?.email) || (sendMethod === 'text' && !client?.phone)}
            >
              {isSending ? (
                <>
                  <Loader size={18} className="spinning" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Estimate
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteConfirmChecked(false);
        }}
        title="Delete Estimate"
      >
        <div className="delete-modal">
          {/* Warning for approved/converted estimates */}
          {(estimate.status === 'approved' || estimate.status === 'converted') && (
            <div className="delete-warning critical">
              <AlertTriangle size={24} />
              <div>
                <strong>Warning: This estimate has been {estimate.status}!</strong>
                <p>
                  {isAdmin 
                    ? 'As an admin, you can delete this estimate, but it may affect related jobs or invoices.'
                    : 'Only administrators can delete approved or converted estimates. Please contact an admin if you need this removed.'}
                </p>
              </div>
            </div>
          )}
          
          {/* Standard warning */}
          <div className="delete-warning">
            <AlertTriangle size={20} />
            <div>
              <strong>This action cannot be undone!</strong>
              <p>Deleting this estimate will permanently remove it from the system.</p>
            </div>
          </div>
          
          {/* Estimate summary */}
          <div className="delete-summary">
            <p><strong>Estimate:</strong> {estimate.title || `Estimate for ${client ? formatFullName(client) : 'Unknown Client'}`}</p>
            <p><strong>Client:</strong> {client ? formatFullName(client) : 'Unknown'}</p>
            <p><strong>Status:</strong> {statusConfig?.label || estimate.status}</p>
            <p><strong>Total:</strong> {formatCurrency(estimate.total || estimate.recurringTotal || 0)}</p>
          </div>
          
          {/* Confirmation checkbox - only show if allowed to delete */}
          {(isAdmin || (estimate.status !== 'approved' && estimate.status !== 'converted')) && (
            <label className="delete-confirm-checkbox">
              <input
                type="checkbox"
                checked={deleteConfirmChecked}
                onChange={(e) => setDeleteConfirmChecked(e.target.checked)}
              />
              <span>I understand this action is permanent and cannot be undone</span>
            </label>
          )}
          
          <div className="modal-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmChecked(false);
              }}
              disabled={isDeleting}
            >
              Cancel
            </button>
            
            {/* Only show delete button if allowed */}
            {(isAdmin || (estimate.status !== 'approved' && estimate.status !== 'converted')) ? (
              <button 
                className="btn btn-danger"
                onClick={handleDeleteEstimate}
                disabled={!deleteConfirmChecked || isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader size={18} className="spinning" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete Estimate
                  </>
                )}
              </button>
            ) : (
              <button className="btn btn-danger" disabled>
                <Trash2 size={18} />
                Admin Only
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default EstimateDetail;
